import { useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import logo from "@/assets/oz-move-logo.webp";
import Footer from "@/components/Footer";

const TermsConditions = () => {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content") ?? "";
    const prevOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute("content") ?? "";
    const prevOgDesc = document.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? "";
    const prevOgUrl = document.querySelector('meta[property="og:url"]')?.getAttribute("content") ?? "";
    const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? "";

    document.title = "Terms & Conditions - OZ MOVE CO.";
    document.querySelector('meta[name="description"]')?.setAttribute(
      "content",
      "OZ MOVE CO. Terms & Conditions. Read the terms that apply to our residential and commercial moving services in Australia."
    );
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", "Terms & Conditions - OZ MOVE CO.");
    document.querySelector('meta[property="og:description"]')?.setAttribute(
      "content",
      "Read the terms that apply to OZ MOVE CO. moving and removals services."
    );
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", "https://ozmoveco.com.au/terms-conditions");
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", "https://ozmoveco.com.au/terms-conditions");

    return () => {
      document.title = prevTitle;
      document.querySelector('meta[name="description"]')?.setAttribute("content", prevDesc);
      document.querySelector('meta[property="og:title"]')?.setAttribute("content", prevOgTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute("content", prevOgDesc);
      document.querySelector('meta[property="og:url"]')?.setAttribute("content", prevOgUrl);
      document.querySelector('link[rel="canonical"]')?.setAttribute("href", prevCanonical);
    };
  }, []);

  const lastUpdated = "29 July 2026";

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f7]">
      {/* Header */}
      <header className="bg-[#FFC300] sticky top-0 z-50">
        <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
          <a href="/" className="shrink-0">
            <img
              src={logo}
              alt="OZ MOVE CO. Logo"
              className="h-14 md:h-20 w-auto"
              width="288"
              height="192"
            />
          </a>
          <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
            <a
              href="tel:1800556654"
              aria-label="Call 1800 556 654"
              className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">1800 556 654</span>
            </a>
            <a
              href="/#quote"
              className="inline-flex bg-[#1a1a1a] text-[#FFC300] px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
            >
              Book Now
            </a>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <section className="py-12 md:py-20">
          <div className="container max-w-3xl">
            <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-[#1a1a1a]/5">
              <p className="text-[#1a1a1a]/60 text-sm mb-4">
                This page is maintained by OZ MOVE CO. to set out the terms that apply to our moving and removals services.
              </p>
              <h1 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-2 leading-tight">
                Terms & Conditions
              </h1>
              <p className="text-[#1a1a1a]/60 text-sm mb-10">
                Last updated: {lastUpdated}
              </p>

              <div className="prose prose-sm max-w-none text-[#1a1a1a]/80 leading-relaxed">
                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">1. Acceptance of Terms</h2>
                <p className="mb-4">
                  By booking a move with OZ MOVE CO., requesting a quote, or using our website, you agree to these Terms & Conditions. If you do not agree, please do not use our services or website. These terms apply to all residential and commercial moving services we provide within Australia.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">2. Services Provided</h2>
                <p className="mb-3">OZ MOVE CO. provides professional moving and removals services, including:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Residential home moves</li>
                  <li>Apartment and unit moves</li>
                  <li>Office and commercial relocations</li>
                  <li>Furniture moving and single-item deliveries</li>
                  <li>Interstate moving services (where available)</li>
                  <li>Packing and unpacking assistance (where offered)</li>
                  <li>Additional services such as furniture disassembly, reassembly, and protective wrapping (as agreed in writing)</li>
                </ul>
                <p className="mb-4">
                  The specific services included in your move will be confirmed at the time of booking. Any service not expressly included in your booking confirmation may incur additional charges.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">3. Quotes</h2>
                <p className="mb-4">
                  Quotes provided by OZ MOVE CO. are estimates unless otherwise stated in writing as a fixed-price agreement. Final pricing may change based on factors including, but not limited to:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Additional items not disclosed at the time of quoting</li>
                  <li>Difficult access, stairs, lifts, narrow hallways, or long carries</li>
                  <li>Delays caused by waiting time, traffic, parking difficulties, or building access issues</li>
                  <li>Changes requested by the customer on the day of the move</li>
                  <li>Last-minute changes to pickup or delivery addresses</li>
                </ul>
                <p className="mb-4">
                  We aim to be transparent about pricing and will communicate any likely additional charges before they are incurred where reasonably practicable.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">4. Bookings</h2>
                <p className="mb-3">A booking is confirmed only when:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>You accept the quote or estimate in writing or via our booking system</li>
                  <li>Any required deposit has been paid and cleared</li>
                  <li>You receive a booking confirmation from OZ MOVE CO.</li>
                </ul>
                <p className="mb-4">
                  Deposits, where required, will be applied toward the total cost of your move. Full payment terms will be set out in your booking confirmation.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">5. Customer Responsibilities</h2>
                <p className="mb-3">To help us deliver a safe and efficient move, you agree to provide:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>An accurate and complete inventory of items to be moved</li>
                  <li>Correct pickup and delivery addresses</li>
                  <li>Accurate information about property access, including stairs, lifts, parking restrictions, and loading zones</li>
                  <li>Suitable parking arrangements for our vehicle at both locations</li>
                  <li>Pets secured and away from the work area during the move</li>
                  <li>Clear identification of fragile, valuable, or unusually heavy items before the move begins</li>
                  <li>Disclosure of any items requiring special handling</li>
                </ul>
                <p className="mb-4">
                  Failure to provide accurate information may result in additional charges, delays, or refusal to move certain items.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">6. Pricing</h2>
                <p className="mb-3">Our pricing structure may include:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Hourly rates for labour and vehicle hire, where applicable</li>
                  <li>Call-out fees or travel charges, where applicable</li>
                  <li>Additional charges for stairs, lifts, heavy items, packing materials, or waiting time</li>
                  <li>Goods and Services Tax (GST), which will be included in all quoted prices unless otherwise stated</li>
                </ul>
                <p className="mb-4">
                  Payment is generally required on completion of the move unless otherwise agreed in writing. We accept payment by cash, card, or other methods as set out in your booking confirmation.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">7. Cancellations & Rescheduling</h2>
                <p className="mb-3">
                  We understand plans can change. Customers are asked to provide reasonable notice for cancellations or rescheduling. Where a deposit has been paid:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Cancellations with reasonable notice may be eligible for a refund or credit</li>
                  <li>Late cancellations or no-shows may result in forfeiture of the deposit</li>
                </ul>
                <p className="mb-4">
                  OZ MOVE CO. reserves the right to cancel or reschedule a booking in exceptional circumstances, such as severe weather, vehicle breakdown, or safety concerns. In such cases, we will notify you as soon as possible and work with you to arrange an alternative.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">8. Delays</h2>
                <p className="mb-3">We aim to arrive and complete every move on time. However, delays may occur due to circumstances beyond our reasonable control, including:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Traffic congestion, road closures, or accidents</li>
                  <li>Severe weather or unsafe driving conditions</li>
                  <li>Lift breakdowns or building access restrictions</li>
                  <li>Customer delays, such as unprepared items or late access to the property</li>
                </ul>
                <p className="mb-4">
                  We will keep you informed of any significant delays and do our best to minimise disruption.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">9. Liability</h2>
                <p className="mb-4">
                  OZ MOVE CO. takes reasonable care when handling your belongings. However, our liability is limited as follows:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>We are not liable for damage that existed before the move or was not visible or identifiable at the time of moving</li>
                  <li>We are not liable for damage caused by inadequate or improper packing by the customer</li>
                  <li>We are not liable for normal wear and tear, including minor scratches, scuffs, or dents that occur during handling</li>
                  <li>We are not liable for loss or damage caused by events outside our reasonable control</li>
                  <li>Our maximum liability is subject to and limited by the Australian Consumer Law</li>
                </ul>
                <p className="mb-4">
                  Any claim for loss or damage must be notified to us in writing as soon as reasonably practicable, and in any event within 48 hours of delivery where possible.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">10. Insurance</h2>
                <p className="mb-4">
                  OZ MOVE CO. carries appropriate public liability and transit insurance in accordance with our business arrangements. Details of any insurance coverage applicable to your move will be provided on request.
                </p>
                <p className="mb-4">
                  We encourage customers with high-value, fragile, or irreplaceable items to consider obtaining additional transit insurance and to declare such items before the move.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">11. Prohibited Items</h2>
                <p className="mb-3">For safety and legal reasons, we will not move the following items without prior written agreement:</p>
                <ul className="list-disc pl-5 space-y-1.5 mb-4">
                  <li>Illegal goods or substances</li>
                  <li>Dangerous chemicals, toxins, or hazardous materials</li>
                  <li>Firearms, explosives, or flammable items</li>
                  <li>Perishable food or plants, unless previously agreed</li>
                  <li>Animals or livestock</li>
                  <li>Items that are unsafe or impractical to move</li>
                </ul>
                <p className="mb-4">
                  If you are unsure whether an item can be moved, please contact us before your booking.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">12. Website Use</h2>
                <p className="mb-4">
                  The content on this website, including text, images, logos, and branding, is owned by or licensed to OZ MOVE CO. and is protected by Australian and international intellectual property laws. You may not copy, reproduce, or distribute our content without our prior written permission.
                </p>
                <p className="mb-4">
                  We make reasonable efforts to ensure the information on our website is accurate and up to date, but we do not guarantee that it is complete, current, or error-free. Our website may contain links to external sites; we are not responsible for the content or practices of those sites.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">13. Australian Consumer Law</h2>
                <p className="mb-4">
                  Nothing in these Terms & Conditions is intended to exclude, restrict, or modify any right or remedy you have under the Australian Consumer Law or any other applicable consumer protection law. To the extent that any term conflicts with a mandatory consumer guarantee, that term will be read down or severed to the minimum extent necessary.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">14. Limitation of Liability</h2>
                <p className="mb-4">
                  To the fullest extent permitted by law, OZ MOVE CO.'s liability for any loss or damage arising from our services or website is limited to the amount paid for the relevant service, or the cost of resupplying the service, whichever is lower. This limitation does not apply to liability that cannot be excluded under the Australian Consumer Law.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">15. Privacy</h2>
                <p className="mb-4">
                  Your privacy is important to us. Please refer to our <a href="/privacy-policy" className="text-[#1a1a1a] font-semibold underline hover:no-underline">Privacy Policy</a> for information about how we collect, use, store, and protect your personal information.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">16. Governing Law</h2>
                <p className="mb-4">
                  These Terms & Conditions are governed by the laws of Victoria, Australia. Any dispute arising from these terms or our services will be subject to the exclusive jurisdiction of the courts of Victoria, subject to any mandatory consumer law rights you may have.
                </p>

                <h2 className="text-xl font-extrabold text-[#1a1a1a] mt-8 mb-3">17. Contact Details</h2>
                <p className="mb-4">
                  If you have any questions about these Terms & Conditions, please contact us using the details below.
                </p>
                <div className="bg-[#f7f7f7] rounded-lg p-6 space-y-3 not-prose">
                  <a
                    href="mailto:info@ozmoveco.com.au"
                    className="flex items-center gap-3 text-[#1a1a1a] font-semibold hover:text-[#1a1a1a]/70 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#1a1a1a]/60" />
                    info@ozmoveco.com.au
                  </a>
                  <a
                    href="tel:1800556654"
                    className="flex items-center gap-3 text-[#1a1a1a] font-semibold hover:text-[#1a1a1a]/70 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-[#1a1a1a]/60" />
                    1800 556 654
                  </a>
                  <div className="flex items-start gap-3 text-[#1a1a1a] font-semibold">
                    <MapPin className="w-5 h-5 text-[#1a1a1a]/60 shrink-0 mt-0.5" />
                    <span>34 King William Street, Broadmeadows VIC 3047, Australia</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions;
